import React, { useState } from 'react';
// ...existing code...
import styles from '../css-isolated/hr_medical_history.module.css';

function InitialHealthAssessment({ values, onChange, onSubmit, isDarkMode = false, activeSection: externalActiveSection, setActiveSection: externalSetActiveSection }) {
  // Active section state (1 = Present Health + Family History, 2 = Past Medical History)
  // Use external state if provided (from HRWizard), otherwise use internal state
  const [internalActiveSection] = useState(1);
  const activeSection = externalActiveSection !== undefined ? externalActiveSection : internalActiveSection;


  // Always provide all familyHistory keys, even if values is empty or missing fields
  const defaultFamilyHistory = {
    hypertension: false,
    stroke: false,
    heartAttack: false,
    diabetes: false,
    asthma: false,
    kidneyDisease: false,
    cancer: false
  };
  const initialFormData = {
    presentHealthConditions: [],
    otherHealthCondition: '',
    pediatricHistory: '',
    majorAdultIllnesses: '',
    majorSurgeries: '',
    seriousInjuries: '',
    limitations: '',
    medicationsHistory: '',
    transfusionHistory: '',
    mentalEmotionalProblems: '',
    bloodType: '',
    allergies: '',
    familyHistory: defaultFamilyHistory,
    otherFamilyHistory: ''
  };
  // Merge values (if any) with defaults, and familyHistory with its defaults
  const mergedFamilyHistory = { ...defaultFamilyHistory, ...(values && values.familyHistory) };
  const mergedFormData = { 
    ...initialFormData, 
    ...values, 
    familyHistory: mergedFamilyHistory,
    presentHealthConditions: values?.presentHealthConditions || [],
    otherHealthCondition: values?.otherHealthCondition || ''
  };
  const [formData, setFormData] = useState(mergedFormData);
  const [showHealthyWarning, setShowHealthyWarning] = useState(false);

  // Predefined health condition options
  const healthConditionOptions = [
    'Healthy', 'Anxiety Disorder', 'Arthritis', 'Asthma',
    'Chronic Obstructive Pulmonary Disease (COPD)', 'Depression',
    'Diabetes Mellitus', 'Epilepsy', 'Gastritis', 'Heart Disease',
    'Hyperventilation', 'Hypertension', 'Kidney Disease', 'Malnutrition',
    'Migraine', 'Pneumonia', 'Skin Disease', 'Tuberculosis'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updated;
    if (type === 'checkbox') {
      updated = {
        ...formData,
        familyHistory: {
          ...formData.familyHistory,
          [name]: checked
        }
      };
    } else {
      updated = {
        ...formData,
        [name]: value
      };
    }
    setFormData(updated);
    if (onChange) onChange(updated);
  };

  // Derived flag
  const isHealthySelected = (formData.presentHealthConditions || []).includes('Healthy');

  // Handler for toggling a health condition checkbox
  const handleHealthConditionToggle = (condition) => {
    const current = formData.presentHealthConditions || [];
    const isSelected = current.includes(condition);

    // Selecting Healthy while other conditions exist → show warning
    if (condition === 'Healthy' && !isSelected && current.length > 0) {
      setShowHealthyWarning(true);
      return;
    }

    // If Healthy is already selected and user picks anything else → block
    if (condition !== 'Healthy' && isHealthySelected) return;

    const newConditions = isSelected
      ? current.filter(c => c !== condition)
      : [...current, condition];
    const updated = { ...formData, presentHealthConditions: newConditions };
    setFormData(updated);
    if (onChange) onChange(updated);
  };

  // Confirm clearing conditions and setting Healthy
  const confirmSelectHealthy = () => {
    const updated = { ...formData, presentHealthConditions: ['Healthy'], otherHealthCondition: '' };
    setFormData(updated);
    if (onChange) onChange(updated);
    setShowHealthyWarning(false);
  };

  // Handler for adding a custom "Other" health condition
  const handleAddOtherCondition = () => {
    if (isHealthySelected) return;
    const trimmed = formData.otherHealthCondition?.trim();
    if (!trimmed) return;
    const current = formData.presentHealthConditions || [];

    // Treat typing "Healthy" (case-insensitive) the same as clicking the checkbox
    if (trimmed.toLowerCase() === 'healthy') {
      if (current.length > 0) {
        // Show the same warning modal
        setShowHealthyWarning(true);
      } else {
        // No other conditions – just set Healthy directly
        const updated = { ...formData, presentHealthConditions: ['Healthy'], otherHealthCondition: '' };
        setFormData(updated);
        if (onChange) onChange(updated);
      }
      return;
    }

    if (current.includes(trimmed)) return; // avoid duplicates
    const updated = { 
      ...formData, 
      presentHealthConditions: [...current, trimmed],
      otherHealthCondition: '' 
    };
    setFormData(updated);
    if (onChange) onChange(updated);
  };

  // Handler for removing a selected health condition
  const handleRemoveCondition = (condition) => {
    const current = formData.presentHealthConditions || [];
    const updated = { 
      ...formData, 
      presentHealthConditions: current.filter(c => c !== condition) 
    };
    setFormData(updated);
    if (onChange) onChange(updated);
  };

  // Handler for "Other" health condition text input
  const handleOtherHealthInput = (e) => {
    const updated = { ...formData, otherHealthCondition: e.target.value };
    setFormData(updated);
    if (onChange) onChange(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert boolean values to integers (0/1) instead of Yes/No strings
    const booleanToInt = (value) => value ? 1 : 0;
    
    // Map the form data to match the backend's expected format
    const mappedData = {
      hr_info_id: values?.hr_info_id, // Make sure hr_info_id is passed from the parent
      presentHealthConditions: formData.presentHealthConditions || [],
      pediatric_history: formData.pediatricHistory || '',
      major_adult_illnesses: formData.majorAdultIllnesses || '',
      major_surgeries: formData.majorSurgeries || '',
      serious_physical_injuries: formData.seriousInjuries || '',
      limit_in_range_of_motion: formData.limitations || '',
      medications_history: formData.medicationsHistory || '',
      history_of_transfusions: formData.transfusionHistory || '',
      mental_emotional_problems: formData.mentalEmotionalProblems || '',
      blood_type: formData.bloodType || '',
      allergies: formData.allergies || '',
      // Convert boolean values directly to 0/1
      fm_hypertension: booleanToInt(formData.familyHistory.hypertension),
      fm_stroke: booleanToInt(formData.familyHistory.stroke),
      fm_heartAttack: booleanToInt(formData.familyHistory.heartAttack),
      fm_diabetes: booleanToInt(formData.familyHistory.diabetes),
      fm_asthma: booleanToInt(formData.familyHistory.asthma),
      fm_kidneyDisease: booleanToInt(formData.familyHistory.kidneyDisease),
      fm_cancer: booleanToInt(formData.familyHistory.cancer),
      fm_others: formData.otherFamilyHistory || ''
    };

    if (onSubmit) onSubmit(mappedData);
  };

  return (
    <div className={`${styles['hr-wizard-container']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
      <div className={styles.assessmentContainer}>
        <div className={styles.contentWrapper}>
          <form onSubmit={handleSubmit}>
            {/* Section 1: Present Health Condition */}
            {activeSection === 1 && (
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>Present Health Condition</h2>
              </div>
              <div className={styles.formGrid}>
                {/* Selected conditions display */}
                {(formData.presentHealthConditions || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {formData.presentHealthConditions.map((condition, idx) => (
                      <span
                        key={idx}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          padding: '0.25rem 0.65rem',
                          borderRadius: '1rem',
                          fontSize: '0.82rem',
                          fontWeight: '500',
                          background: 'rgba(59, 130, 246, 0.15)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          color: isDarkMode ? '#93c5fd' : '#2563eb'
                        }}
                      >
                        {condition}
                        <button
                          type="button"
                          onClick={() => handleRemoveCondition(condition)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '1rem',
                            lineHeight: '1',
                            opacity: 0.7
                          }}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Healthy warning modal */}
                {showHealthyWarning && (
                  <div style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    background: 'rgba(0,0,0,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <div style={{
                      background: isDarkMode ? '#1e293b' : '#fff',
                      border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                      borderRadius: '0.75rem',
                      padding: '1.75rem 2rem',
                      maxWidth: '420px',
                      width: '90%',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                          Replace Health Conditions?
                        </h3>
                      </div>
                      <p style={{ margin: '0 0 1.25rem', fontSize: '0.875rem', color: isDarkMode ? '#94a3b8' : '#64748b', lineHeight: '1.5' }}>
                        Selecting <strong>Healthy</strong> will remove all currently added health conditions. Do you want to proceed?
                      </p>
                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => setShowHealthyWarning(false)}
                          style={{
                            padding: '0.5rem 1.1rem',
                            borderRadius: '0.375rem',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1',
                            background: 'transparent',
                            color: isDarkMode ? '#94a3b8' : '#64748b',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={confirmSelectHealthy}
                          style={{
                            padding: '0.5rem 1.1rem',
                            borderRadius: '0.375rem',
                            border: 'none',
                            background: '#ef4444',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          Yes, Set as Healthy
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Health condition checkboxes */}
                <div className={styles.familyHistoryGrid}>
                  {healthConditionOptions.map(condition => {
                    const isChecked = (formData.presentHealthConditions || []).includes(condition);
                    const isDisabled = condition !== 'Healthy' && isHealthySelected;
                    return (
                      <label
                        key={condition}
                        className={styles.familyHistoryOption}
                        style={isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleHealthConditionToggle(condition)}
                          disabled={isDisabled}
                          className={styles.familyHistoryCheckbox}
                        />
                        <span className={styles.familyHistoryLabel}>
                          {condition}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {/* Other condition input */}
                <div className={styles.formRow} style={{ marginTop: '0.75rem' }}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Other Health Condition</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        value={formData.otherHealthCondition || ''}
                        onChange={handleOtherHealthInput}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOtherCondition(); } }}
                        placeholder={isHealthySelected ? 'Unavailable when Healthy is selected' : 'Type other condition...'}
                        className={styles.formInput}
                        disabled={isHealthySelected}
                        style={{ flex: 1, opacity: isHealthySelected ? 0.5 : 1 }}
                      />
                      <button
                        type="button"
                        onClick={handleAddOtherCondition}
                        disabled={isHealthySelected}
                        style={{
                          padding: '0.4rem 1rem',
                          borderRadius: '0.375rem',
                          border: '1px solid rgba(59, 130, 246, 0.4)',
                          background: 'rgba(59, 130, 246, 0.15)',
                          color: isDarkMode ? '#93c5fd' : '#2563eb',
                          cursor: isHealthySelected ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          whiteSpace: 'nowrap',
                          opacity: isHealthySelected ? 0.5 : 1
                        }}
                      >
                        + Add
                      </button>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.requiredLabel}`}>Blood Type</label>
                    <select
                      name="bloodType"
                      value={formData.bloodType}
                      onChange={handleInputChange}
                      className={styles.formInput}
                    >
                      <option value="">Select Blood Type</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty for layout balance */}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Family History - Now on Page 1 */}
            {activeSection === 1 && (
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>Family History (1° degree)</h2>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.familyHistoryGrid}>
                  {[
                    { name: 'hypertension', label: 'Hypertension' },
                    { name: 'stroke', label: 'Stroke' },
                    { name: 'heartAttack', label: 'Heart Attack' },
                    { name: 'diabetes', label: 'Diabetes' },
                    { name: 'asthma', label: 'Asthma' },
                    { name: 'kidneyDisease', label: 'Kidney Disease' },
                    { name: 'cancer', label: 'Cancer' }
                  ].map(condition => (
                    <label key={condition.name} className={styles.familyHistoryOption}>
                      <input
                        type="checkbox"
                        name={condition.name}
                        checked={formData.familyHistory[condition.name]}
                        onChange={handleInputChange}
                        className={styles.familyHistoryCheckbox}
                      />
                      <span className={styles.familyHistoryLabel}>
                        {condition.label}
                      </span>
                    </label>
                  ))}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Other Family History</label>
                    <textarea
                      name="otherFamilyHistory"
                      value={formData.otherFamilyHistory}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Additional family history..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty for layout balance */}
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty for layout balance */}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Section 2: Past Medical History */}
            {activeSection === 2 && (
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>Past Medical History</h2>
              </div>
              <div className={styles.formGrid}>
                {/* Compact Three Column Layout */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Pediatric History</label>
                    <textarea
                      name="pediatricHistory"
                      value={formData.pediatricHistory}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Childhood illnesses..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Major Adult Illnesses</label>
                    <textarea
                      name="majorAdultIllnesses"
                      value={formData.majorAdultIllnesses}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Adult illnesses..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Major Surgeries</label>
                    <textarea
                      name="majorSurgeries"
                      value={formData.majorSurgeries}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Surgeries/hospitalization..."
                    />
                  </div>
                </div>

                {/* Second Row */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Physical Injuries</label>
                    <textarea
                      name="seriousInjuries"
                      value={formData.seriousInjuries}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Serious injuries..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Motion Limitations</label>
                    <textarea
                      name="limitations"
                      value={formData.limitations}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Range of motion limits..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Medications</label>
                    <textarea
                      name="medicationsHistory"
                      value={formData.medicationsHistory}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Medication history..."
                    />
                  </div>
                </div>

                {/* Third Row */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Transfusions/BT</label>
                    <textarea
                      name="transfusionHistory"
                      value={formData.transfusionHistory}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Blood transfusions..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Mental Health</label>
                    <textarea
                      name="mentalEmotionalProblems"
                      value={formData.mentalEmotionalProblems}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Mental/emotional issues..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Allergies</label>
                    <textarea
                      name="allergies"
                      value={formData.allergies}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Known allergies..."
                    />
                  </div>
                </div>
              </div>
            </div>
            )}

          </form>
        </div>
      </div>
    </div>
  );
}

export default InitialHealthAssessment;